import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';

import { CreateKeyForm } from '../hooks/useKeyManagement';

// Move this utility function outside to prevent recreation on each render
const getDefaultExpiryDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().split('T')[0];
};

interface KeyFormState {
  name: string;
  hasExpiration: boolean;
  expiresAt: string;
}

interface CreateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateKeyForm) => Promise<void>;
}

export function CreateKeyModal({ isOpen, onClose, onSubmit }: CreateKeyModalProps) {
  const [formState, setFormState] = useState<KeyFormState>({
    name: '',
    hasExpiration: false,
    expiresAt: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setFormState({
      name: '',
      hasExpiration: false,
      expiresAt: '',
    });
  }, []);

  // Reset form when modal is opened
  useEffect(() => {
    if (isOpen) {
      resetForm();
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, resetForm]);

  const handleExpirationChange = (checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      hasExpiration: checked,
      expiresAt: checked ? getDefaultExpiryDate() : '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: formState.name,
        expires_at:
          formState.hasExpiration && formState.expiresAt
            ? `${formState.expiresAt}T23:59:59Z`
            : null,
      });
      // Reset form
      resetForm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="create-key-title"
      className="relative z-[60]"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className="mx-auto max-w-md rounded bg-white dark:bg-gray-800 p-6 w-full"
          role="dialog"
        >
          <div className="flex justify-between items-center mb-4">
            <DialogTitle
              id="create-key-title"
              className="text-lg font-medium text-gray-900 dark:text-gray-100"
            >
              Create New API Key
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              data-ph-capture-attribute="btn-create-api-key-modal-close"
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Create API key form">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                id="key-name-label"
              >
                Key Name{' '}
                <span className="text-red-500 dark:text-red-400" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                ref={inputRef}
                type="text"
                id="name"
                name="name"
                value={formState.name}
                onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                required
                data-ph-capture-attribute="input-api-key-name"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#E34290] focus:ring-[#E34290] dark:bg-gray-700 dark:text-gray-100 sm:text-sm px-3 py-2"
                placeholder="e.g. Production API Key"
                aria-labelledby="key-name-label"
                aria-required="true"
              />
            </div>

            <div role="group" aria-labelledby="expiration-group-label">
              <div className="flex items-center mb-2">
                <span
                  id="expiration-group-label"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-3"
                >
                  Expiration Date
                </span>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasExpiration"
                    checked={formState.hasExpiration}
                    onChange={(e) => handleExpirationChange(e.target.checked)}
                    data-ph-capture-attribute="input-api-key-expiration-toggle"
                    className="rounded border-gray-300 dark:border-gray-600 text-[#E34290] focus:ring-[#E34290] dark:bg-gray-700"
                    aria-controls="expiration-date-input"
                    aria-expanded={formState.hasExpiration}
                  />
                  <label
                    htmlFor="hasExpiration"
                    className="ml-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    Set expiration
                  </label>
                </div>
              </div>
              {formState.hasExpiration && (
                <div id="expiration-date-input">
                  <input
                    type="date"
                    id="expires"
                    name="expires"
                    value={formState.expiresAt}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        expiresAt: e.target.value,
                      }))
                    }
                    data-ph-capture-attribute="input-api-key-expiration-date"
                    min={new Date().toISOString().split('T')[0]}
                    required={formState.hasExpiration}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#E34290] focus:ring-[#E34290] dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
                    aria-label="Expiration date"
                    aria-required={formState.hasExpiration}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                aria-label="Cancel creating API key"
                data-ph-capture-attribute="btn-api-key-create-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !formState.name ||
                  (formState.hasExpiration && !formState.expiresAt)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-[#E34290] rounded-md hover:bg-[#E34290]/80 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed dark:disabled:bg-gray-700 dark:disabled:text-gray-500"
                aria-label={isSubmitting ? 'Creating API key...' : 'Create API key'}
                aria-busy={isSubmitting}
                data-ph-capture-attribute="btn-api-key-create-submit"
              >
                {isSubmitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
